import { LoadingOutlined } from '@ant-design/icons';
import { UseMutateFunction, useMutation } from '@tanstack/react-query';
import { Col, Form, Image, MenuProps, Modal, Row, Space, Table } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import classNames from 'classnames';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { SalesInfoApis, TAllocateUpdateJsonParams } from 'apis/SalesInfoApis';
import { EditIcon } from 'assets/svg';
import { Button, ButtonAction, InputForm, SelectForm } from 'components';
import { ExportOptionsButton } from 'components/Button/ExportOptionsButton';
import { OptionsMenu } from 'components/OptionsMenu/OptionsMenu';
import { useUser } from 'contexts/UserProvider';
import {
  IAllocatePriceParams,
  ICustomer,
  IDataGrouped,
  IGoldSoldBuyOrder,
  IGoldSoldYBuyOrder,
  IOrder,
  IOrderStatus,
  IPromotion,
  IPromotionResult,
  IUpdateOrderTempParams,
  OrderTempDetail,
  OrderTempDetailY,
  Result,
} from 'interfaces/SalesInfo';
import formatNumber, {
  convertDateToBE,
  formatCurrency,
  formatDate,
  toastError,
  toastSuccess,
} from 'utils/constants/functions';
import CustomCollapse, {
  IPanelConfig,
} from '../../components/Collapse/CustomCollapse';
import { PurChaseInvoiceApi } from 'apis/PurchaseInvoiceApis';
import dayjs from 'dayjs';
import DebtApis from 'apis/DebtApis';
import { useAmlCheck } from 'hooks/useAmlCheck';

const sellTotalKeys = [
  'goldPrice',
  'processPrice',
  'processPriceSap',
  'finalPrice',
  'itemWeightSeed',
  'goldWeightOfStool',
  'totalPriceSap',
  'finalPriceSap',
  'quantity',
];
const sellMoneyKeys = [
  // 'goldPrice',
  // 'processPrice',
  // 'processPriceSap',
  // 'finalPrice',
  // 'totalPriceSap',
  // 'finalPriceSap',
  'totalGoldPrice',
  'totalProcessPrice',
  'totalPrice',
  'finalPrice',
  'processPriceSap',
  'totalPriceSap',
  'finalPriceSap',
];
// const sellNumberKeys = ['itemWeightSeed', 'goldWeightOfStool'];
const sellNumberKeys = ['totalItemWeightSeed', 'totalGoldWeightOfStool'];
const formatSellDataIndex = {
  // goldPrice: 'text-right',
  // processPrice: 'text-right',
  // processPriceSap: 'text-right',
  // priceM: 'text-right',
  // finalPrice: 'text-right',
  // totalPriceSap: 'text-right',
  // totalPrice: 'text-right',
  // finalPriceSap: 'text-right',
  totalGoldPrice: 'text-right',
  totalProcessPrice: 'text-right',
  processPriceSap: 'text-right',
  priceM: 'text-right',
  finalPrice: 'text-right',
  totalPriceSap: 'text-right',
  totalPrice: 'text-right',
  finalPriceSap: 'text-right',
} as any;
const sellDataIndexes = [
  // 'urlImage',
  // 'id',
  // 'name',
  // 'goldPrice',
  // 'processPrice',
  // 'totalPrice',
  // 'finalPrice',
  // 'haolaiCost',
  // 'age',
  // 'itemWeightSeed',
  // 'goldWeightOfStool',
  // 'processPriceSap',
  // 'totalPriceSap',
  // 'finalPriceSap',
  // 'quantity',
  // 'batch',
  // 'sloc',
  // '1',
  // 'voucherName',
  // '2',
  'urlImage',
  'id',
  'name',
  '1',
  '2',
  'totalGoldPrice',
  'totalProcessPrice',
  'totalPrice',
  'finalPrice',
  'haolaiCost',
  'age',
  'totalItemWeightSeed',
  'totalGoldWeightOfStool',
  'processPriceSap',
  'totalPriceSap',
  'finalPriceSap',
  'totalQuantity',
  'batch',
  'sloc',
  'voucherName',
];

const goldSoldTotalKeys = [
  // 'quantity',
  // 'itemWeightSeed',
  // 'goldWeightOfStool',
  // 'itemWeightOfStool',
  // 'hao',
  // 'q10',
  'quantity',
  'itemWeightSeed',
  'goldWeightOfStool',
  'itemWeightOfStool',
  'hao',
  'q10',

  // 'totalQuantity',
  // 'totalItemWeightSeed',
  // 'totalGoldWeightOfStool',
  // 'totalItemWeightOfStool',
  // 'totalHao',
  // 'totalQ10',
];
const goldSoldNumberKeys = [
  // 'itemWeightSeed',
  // 'goldWeightOfStool',
  // 'itemWeightOfStool',
  // 'q10',
  // 'hao',
  // 'quantity',
  'itemWeightSeed',
  'goldWeightOfStool',
  'itemWeightOfStool',
  'hao',
  'q10',

  // 'totalItemWeightSeed',
  // 'totalGoldWeightOfStool',
  // 'totalItemWeightOfStool',
  // 'totalHao',
  // 'totalQ10',
];

const colSpan = 8;

interface ISalesInfoTabProps {
  orderData: IOrder;
  customer: ICustomer;
  hasEInvoice?: boolean;
  hasSODOBilling?: boolean;
  isChildrenOrder?: boolean;
  goldSoldBuyOrderData: IGoldSoldBuyOrder | IGoldSoldYBuyOrder;
  promotionData: IPromotion;
  optionMenuItems: MenuProps['items'];
  orderStatus: IOrderStatus;
  isOrderOfAccountant: boolean;
  promotion: {
    data: {
      SAPPromotions: IDataGrouped[];
      vouchers: IPromotionResult['results'];
      manualVouchers: IDataGrouped[];
    };
  };
  exportMenuProps: MenuProps;
  refetchOrder?(params: { type?: 'init' | 'updatedCustomer' }): void;
  onCreateOrder: UseMutateFunction<any, Error, IAllocatePriceParams, unknown>;
  onTakeAllocatePrice(
    params: IAllocatePriceParams & { afterMutateSuccess?: () => void },
  ): void;
}

export const SalesInfoTab = memo(
  ({
    customer,
    orderData,
    promotionData,
    optionMenuItems,
    goldSoldBuyOrderData,
    isChildrenOrder = false,
    hasEInvoice = false,
    hasSODOBilling = false,
    promotion,
    isOrderOfAccountant,
    exportMenuProps,
    orderStatus,
    refetchOrder,
    onCreateOrder,
    onTakeAllocatePrice,
  }: ISalesInfoTabProps) => {
    const { user } = useUser();
    const [form] = Form.useForm();
    const { data: tableData } = promotion;

    const [goldSoldBuyOrder, setGoldSoldBuyOrder] = useState(
        [] as (OrderTempDetail | OrderTempDetailY)[],
      ),
      [pageInfo, setPageInfo] = useState({
        page: 1,
        pageSize: 10,
      }),
      [isOpenCustomerModal, setIsOpenCustomerModal] = useState(false),
      [customerList, setCustomerList] = useState([] as any[]),
      [enableCreateOrder, setEnableCreateOrder] = useState(false),
      [salesLimitData, setSalesLimitData] = useState<any>(null);

    const { checkAndProceed, amlModalContext } = useAmlCheck();

    // Fetch sales limit data
    const { mutate: getSalesLimitMutate, isPending: isGetSalesLimitPending } =
      useMutation({
        mutationFn: async ({
          customerId,
          plantId,
        }: {
          customerId: string;
          plantId: string;
        }) => {
          console.log(
            '🔍 About to call DebtApis.getRuleCustomerDebitDetail with:',
            { customerId, plantId },
          );
          try {
            const result = await DebtApis.getRuleCustomerDebitDetail({
              customerId,
              plantId,
            });
            console.log(
              '🔍 DebtApis.getRuleCustomerDebitDetail result:',
              result,
            );
            return result;
          } catch (error) {
            console.error('🔍 Error in mutationFn:', error);
            throw error;
          }
        },
        onSuccess: (res: any) => {
          console.log('🔍 Sales limit API success - full response:', res);
          console.log(
            '🔍 Sales limit data:',
            res?.data?.getRuleCustomerDebitDetail,
          );
          const data = res?.data?.getRuleCustomerDebitDetail;
          console.log('🔍 Setting salesLimitData to:', data);
          setSalesLimitData(data);
          console.log('🔍 salesLimitData set, should trigger panels useMemo');
        },
        onError: (error) => {
          console.error('🔍 Sales limit API error:', error);
          setSalesLimitData(null);
        },
        onSettled: () => {
          console.log('🔍 Sales limit API settled (success or error)');
        },
      });

    const {
        mutate: getCustomerListMutate,
        isPending: isGetCustomerListPending,
      } = useMutation({
        mutationFn: (customerId: string) =>
          SalesInfoApis.getCustomerList(customerId),
        onSuccess: (res: any) => {
          if (res?.data?.getCustomerList && customer) {
            const items = [
              customer,
              ...(res?.data?.getCustomerList?.items ?? []),
            ];
            const newItems = Array.from(
              new Set(items.map((item) => item?.id)),
            ).map((id) => items.find((item) => item?.id === id));

            setCustomerList(
              newItems.map((customer: any) => ({
                ...customer,
                sapCustomerId: customer?.sapCustomerId || customer?.id,
                trankName: customer?.trank?.name,
              })),
            );
            return;
          }

          if (res?.errors?.length > 0)
            toastError(
              `Lỗi khi lấy danh sách khách hàng liên quan. ${res?.errors?.[0]?.message}`,
            );
        },
        onError: (error) => {
          toastError(
            `Lỗi khi lấy danh sách khách hàng liên quan. ${error?.message ?? error}`,
          );
        },
      }),
      {
        mutate: updateOrderTempMutate,
        isPending: isUpdateOrderTempMutatePending,
      } = useMutation({
        mutationFn: (params: IUpdateOrderTempParams) =>
          SalesInfoApis.updateOrderTemp(params),
        onSuccess: async (res: any) => {
          if (res?.data?.updateOrderTemp?.id) {
            await SalesInfoApis.allocateUpdateJson({
              Key: orderData.id,
            } as TAllocateUpdateJsonParams);
            await SalesInfoApis.resetOrder(orderData.id);
            refetchOrder && refetchOrder({ type: 'updatedCustomer' });

            setIsOpenCustomerModal(false);
            toastSuccess('Cập nhật khách hàng thành công!');
            return;
          }

          toastError(
            `Cập nhật khách hàng thất bại. ${res?.errors[0]?.message}`,
          );
        },
        onError: (error) => {
          toastError(
            `Cập nhật khách hàng thất bại. ${error?.message ?? error}`,
          );
        },
      });

    // const exportMenuProps = useMemo(() => {
    //   return {
    //     items: [
    //       {
    //         label: 'Xuất excel',
    //         key: '1',
    //         handleExport: () => console.log('Xuất excel'),
    //       },
    //       {
    //         label: 'Xuất PDF',
    //         key: '2',
    //         handleExport: () => console.log('Xuất PDF'),
    //       },
    //       {
    //         label: 'In',
    //         key: '3',
    //         handleExport: () => console.log('In'),
    //       },
    //     ],
    //   } as TMenuPropsCustom;
    // }, []);

    const optionMenuProps = useMemo(() => {
      return {
        // items: [
        //   {
        //     label: 'Gộp đơn',
        //     key: '1',
        //     onClick: () => console.log('Gộp đơn'),
        //   },
        //   {
        //     label: 'Tách đơn',
        //     key: '2',
        //     onClick: () => console.log('Tách đơn'),
        //   },
        //   {
        //     label: 'Thủ kho xác nhận',
        //     key: '3',
        //     onClick: () => console.log('Thủ kho xác nhận'),
        //   },
        //   {
        //     label: 'Thủ quỹ xác nhận',
        //     key: '4',
        //     onClick: () => console.log('Thủ quỹ xác nhận'),
        //   },
        //   {
        //     label: 'Thương lượng giá',
        //     key: '5',
        //     onClick: () => console.log('Thương lượng giá'),
        //   },
        //   {
        //     label: 'In hoá đơn điện tử',
        //     key: '6',
        //     onClick: () => console.log('In hoá đơn điện tử'),
        //   },
        //   {
        //     label: 'Huỷ tách',
        //     key: '7',
        //     onClick: () => console.log('Huỷ tách'),
        //   },
        //   {
        //     label: 'Huỷ gộp',
        //     key: '8',
        //     onClick: () => console.log('Huỷ gộp'),
        //   },
        //   {
        //     label: 'Huỷ chứng từ SAP',
        //     key: '9',
        //     onClick: () => console.log('Huỷ chứng từ SAP'),
        //   },
        // ],
        items: optionMenuItems,
      };
    }, [optionMenuItems]);

    const panels = useMemo(() => {
      console.log(
        '🔍 Panels useMemo triggered - salesLimitData:',
        !!salesLimitData,
        'loading:',
        isGetSalesLimitPending,
      );
      return [
        {
          key: 'customer',
          title: 'Khách hàng',
          tableProps: {
            scroll: { x: 1700 },
            // loading: isGetCustomerPending || isGetGoldSoldBuyOrderPending,
          },
          columns: [
            {
              title: 'Mã KH',
              dataIndex: 'sapCustomerId',
              className: 'text-start align-top !pl-[30px]',
              width: 100,
            },
            {
              title: 'Tên KH',
              dataIndex: 'storeName',
              className: 'text-start align-top',
              width: 200,
            },
            {
              title: 'Loại',
              dataIndex: ['trank', 'name'],
              className: 'text-start align-top',
              width: 100,
            },
            {
              title: 'MST',
              dataIndex: 'taxId',
              className: 'text-start align-top',
              width: 100,
              render: (value) => {
                return value ? value : '-';
              },
            },
            {
              title: 'Người đại diện',
              dataIndex: 'mainCustomerId',
              className: 'text-start align-top',
              width: 100,
            },
            {
              title: 'SDT',
              dataIndex: 'phone',
              className: 'text-start align-top',
              width: 100,
            },
            {
              title: 'Địa chỉ',
              dataIndex: 'address',
              className: 'text-start align-top',
              width: 300,
            },
            {
              fixed: 'right',
              align: 'center',
              width: 50,
              render: (_: any, record: any, __: number) => {
                return (
                  <Space size={'middle'} className="text-lg ">
                    <ButtonAction
                      disabled={
                        hasSODOBilling ||
                        isChildrenOrder ||
                        promotionData?.IsEInvoicePending ||
                        promotionData?.IsAllocatePricePending ||
                        !isOrderOfAccountant ||
                        user?.isAdmin
                      }
                      tooltip="Chỉnh sửa"
                      onClick={() => {
                        setIsOpenCustomerModal(true);

                        getCustomerListMutate(record?.id);
                      }}
                    >
                      <EditIcon />
                    </ButtonAction>
                  </Space>
                );
              },
            },
          ],
          data: [customer],
        },
        {
          key: 'sales-limit',
          title: 'Hạn mức xuất bán',
          columns: [
            {
              title: 'Hạn mức / lần xuất bán',
              children: [
                {
                  title: 'Q10',
                  dataIndex: 'goldThreadSalesLimit',
                  width: 80,
                  align: 'center' as const,
                  render: (value: any) => value || '-',
                },
                {
                  title: 'Tiền mặt',
                  dataIndex: 'amountSalesLimit',
                  width: 100,
                  align: 'center' as const,
                  render: (value: any) =>
                    formatCurrency({ value: value || 0, isVND: true }),
                },
              ],
            },
            {
              title: 'Tổng nợ khách hàng',
              children: [
                {
                  title: 'VND',
                  dataIndex: 'totalCustomerDebtAmount',
                  width: 120,
                  align: 'right' as const,
                  render: (value: any) =>
                    formatCurrency({ value: value || 0, isVND: true }),
                },
                {
                  title: 'Ngày đến hạn',
                  dataIndex: 'totalCustomerDebtDays',
                  width: 100,
                  align: 'center' as const,
                  render: (value: any) => (
                    <span
                      style={{
                        color: value && value > 0 ? '#ff4d4f' : 'inherit',
                      }}
                    >
                      {value || 0}
                    </span>
                  ),
                },
              ],
            },
            {
              title: 'Q10 Hàng Truyền thống',
              children: [
                {
                  title: 'Q10 có thể bán',
                  dataIndex: 'q10TradableTraditional',
                  width: 120,
                  align: 'right' as const,
                  render: (value: any) => Number(value || 0).toFixed(4),
                },
                {
                  title: 'Ngày đến hạn',
                  dataIndex: 'traditionalQ10ExpiredDays',
                  width: 100,
                  align: 'center' as const,
                  render: (value: any) => {
                    if (value && value !== '-') {
                      return new Date(value).toLocaleDateString('vi-VN');
                    }
                    return value || '-';
                  },
                },
              ],
            },
            {
              title: 'Tiền công Hàng Truyền thống',
              children: [
                {
                  title: 'VND có thể bán',
                  dataIndex: 'vndTradable_01',
                  width: 120,
                  align: 'right' as const,
                  render: (value: any) =>
                    formatCurrency({ value: value || 0, isVND: true }),
                },
                {
                  title: 'Ngày đến hạn',
                  dataIndex: 'traditionalWagesExpiredDays',
                  width: 100,
                  align: 'center' as const,
                  render: (value: any) => {
                    return value || '-';
                  },
                },
              ],
            },
            {
              title: 'Hàng Cao cấp',
              children: [
                {
                  title: 'VND có thể bán',
                  dataIndex: 'vndTradable_02',
                  width: 120,
                  align: 'right' as const,
                  render: (value: any) =>
                    formatCurrency({ value: value || 0, isVND: true }),
                },
                {
                  title: 'Ngày đến hạn',
                  dataIndex: 'premiumExpiredDays',
                  width: 100,
                  align: 'center' as const,
                  render: (value: any) => {
                    return value || '-';
                  },
                },
              ],
            },
            {
              title: 'Hàng Cao cấp và Tiền công Hàng Truyền Thống',
              children: [
                {
                  title: 'VND có thể bán',
                  dataIndex: 'vndTradable_03',
                  width: 140,
                  align: 'right' as const,
                  render: (value: any) => value || '-',
                },
                {
                  title: 'Ngày đến hạn',
                  dataIndex: 'combinedExpiredDays',
                  width: 100,
                  align: 'center' as const,
                  render: (value: any) => {
                    return value || '-';
                  },
                },
              ],
            },
            {
              title: '',
              children: [
                {
                  title: 'Tiền tệ',
                  dataIndex: 'currency',
                  width: 80,
                  align: 'center' as const,
                  render: (value: any) => value || 'VND',
                },
                {
                  title: 'Đơn vị Q10',
                  dataIndex: 'q10Unit',
                  width: 80,
                  align: 'center' as const,
                  render: (value: any) => value || 'Chỉ',
                },
              ],
            },
          ],
          data: salesLimitData
            ? [
                {
                  key: '1',
                  // Hạn mức / lần xuất bán
                  goldThreadSalesLimit:
                    salesLimitData?.ruleCustomerDebit?.goldThreadSalesLimit ||
                    0,
                  cash: 100, // Default value as shown in image
                  amountSalesLimit:
                    salesLimitData?.ruleCustomerDebit?.amountSalesLimit || 0,
                  totalCustomerDebtAmount:
                    (salesLimitData?.customerDebtAmountDEB10_01 || 0) +
                    (salesLimitData?.customerDebtAmountDEB10_02 || 0) +
                    (salesLimitData?.customerDebtAmountDEB10_03 || 0),
                  totalCustomerDebtDays: (() => {
                    const expiredDates = [
                      salesLimitData?.expiredDate_01,
                      salesLimitData?.expiredDate_02,
                      salesLimitData?.expiredDate_03,
                    ].filter(Boolean);

                    if (expiredDates.length === 0) return 0;

                    // Lấy ngày gần nhất (gần hôm nay nhất)
                    const now = new Date();
                    const closestDate = expiredDates.reduce(
                      (closest, current) => {
                        const currentDate = new Date(current);
                        const closestDateObj = new Date(closest);

                        const currentDiff = Math.abs(
                          currentDate.getTime() - now.getTime(),
                        );
                        const closestDiff = Math.abs(
                          closestDateObj.getTime() - now.getTime(),
                        );

                        return currentDiff < closestDiff ? current : closest;
                      },
                    );

                    return new Date(closestDate).toLocaleDateString('vi-VN');
                  })(),
                  salesLimitExpiredDays: 0, // Default as shown in image

                  // Q10 Hàng Truyền thống
                  q10TradableTraditional: salesLimitData?.q10Tradable || '-', // Sử dụng q10Tradable từ API
                  traditionalQ10ExpiredDays:
                    salesLimitData?.expiredDateQ10 || '-',

                  // Tiền công Hàng Truyền thống
                  vndTradable_01: salesLimitData?.vndTradable_01 || 0,
                  traditionalWagesExpiredDays: (() => {
                    const expiredDate_01 = salesLimitData?.expiredDate_01;
                    const customerDebtAmount =
                      salesLimitData?.customerDebtAmountDEB10_01 || 0;

                    if (expiredDate_01 && customerDebtAmount > 0) {
                      return new Date(expiredDate_01).toLocaleDateString(
                        'vi-VN',
                      );
                    }
                    return '-';
                  })(),

                  // Hàng Cao cấp
                  vndTradable_02: salesLimitData?.vndTradable_02 || 0,
                  premiumExpiredDays: (() => {
                    const expiredDate_02 = salesLimitData?.expiredDate_02;
                    const customerDebtAmount =
                      salesLimitData?.customerDebtAmountDEB10_02 || 0;

                    if (expiredDate_02 && customerDebtAmount > 0) {
                      return new Date(expiredDate_02).toLocaleDateString(
                        'vi-VN',
                      );
                    }
                    return '-';
                  })(),

                  // Hàng Cao cấp và Tiền công Hàng Truyền Thống
                  vndTradable_03: salesLimitData?.vndTradable_03 || null,
                  combinedExpiredDays: (() => {
                    const expiredDate_03 = salesLimitData?.expiredDate_03;
                    const customerDebtAmount =
                      salesLimitData?.customerDebtAmountDEB10_03 || 0;

                    if (expiredDate_03 && customerDebtAmount > 0) {
                      return new Date(expiredDate_03).toLocaleDateString(
                        'vi-VN',
                      );
                    }
                    return '-';
                  })(),
                  currency: 'VND',
                  q10Unit: 'Chỉ',
                },
              ]
            : [],
          tableProps: {
            loading: isGetSalesLimitPending,
            pagination: false,
            bordered: true,
            size: 'small',
            scroll: { x: 1200 },
          },
        },
        {
          key: 'price',
          title: 'Giá bóng',
          tableProps: { scroll: { x: 1550 } },
          columns: [
            {
              title: 'Giá bóng',
              dataIndex: 'marketGoldPrice',
              width: 100,
              currency: 'vi-VN',
              align: 'right',
              className: 'text-start align-top',
              render: (value) => {
                const isValid = value !== null && value !== undefined;

                return isValid ? formatCurrency({ value, round: true }) : '-';
              },
            },
            {
              title: 'Giá bóng thương lượng',
              dataIndex: 'dealPrice',
              currency: 'vi-VN',
              width: 250,
              align: 'right',
              className: 'text-start align-top',
              render: (value) => {
                const isValid = value !== null && value !== undefined;

                return isValid ? formatCurrency({ value, round: true }) : '-';
              },
            },
            {
              title: 'Ghi chú',
              dataIndex: 'note',
              className: 'text-start align-top',
            },
            {
              title: 'Người gửi',
              dataIndex: 'createName',
              className: 'text-start align-top',
            },
            {
              title: 'Thời gian gửi',
              dataIndex: 'createDate',
              className: 'text-start align-top',
              render: (value) => {
                return value && formatDate(value);
              },
            },
            {
              title: 'Người duyệt',
              dataIndex: ['approvalDetail', [0], 'tcreateUser', 'fullName'],
              className: 'text-start align-top',
            },
            {
              title: 'Thời gian duyệt',
              dataIndex: ['approvalDetail', [0], 'createDate'],
              className: 'text-start align-top',
              render: (value) => {
                return value && formatDate(value);
              },
            },
            // {
            //   fixed: 'right',
            //   align: 'center',
            //   width: 50,
            //   render: (_: any, record: any, index: number) => {
            //     return (
            //       <ButtonAction
            //         tooltip="Xem chi tiết"
            //         onClick={() => console.log(record, index)}
            //       >
            //         <EyeIcon />
            //       </ButtonAction>
            //     );
            //   },
            // },
          ],
          data: [
            {
              marketGoldPrice: orderData?.marketGoldPrice,
              ...orderData?.dealPrice,
            },
          ],
        },
        {
          key: 'payment',
          title: 'Thanh toán',
          tableProps: { scroll: { x: 1550 } },
          columns: [
            {
              title: 'SL Sản phẩm',
              dataIndex: 'totalQuantity',
              align: 'right',
              width: 30,
            },
            {
              title: 'Q10',
              dataIndex: 'totalQ10',
              className: 'text-start align-top',
              width: 100,
              render: (value) => {
                const newValue =
                  'totalQ10' in goldSoldBuyOrderData
                    ? value
                    : orderData.totalQ10;
                return Number(newValue || 0).toFixed(3);
              },
            },
            {
              title: 'Tiền món',
              dataIndex: 'totalPrice',
              align: 'right',
              currency: 'vi-VN',
              width: 100,
              render: (value, record: any) => {
                const isValid =
                  value !== null &&
                  value !== undefined &&
                  record?.orderTempDetail[0]?.tproduct?.ttype?.id !== 'T';

                return isValid
                  ? formatCurrency({
                      value,
                      round: true,
                    })
                  : '-';
              },
            },
            {
              title: 'Tiền công',
              dataIndex: 'totalProcessPrice',
              align: 'right',
              currency: 'vi-VN',
              width: 100,
              render: (value, record: any) => {
                const isValid =
                  value !== null &&
                  value !== undefined &&
                  record?.orderTempDetail[0]?.tproduct?.ttype?.id === 'T';

                return isValid
                  ? formatCurrency({
                      value,
                      round: true,
                    })
                  : '-';
              },
            },
            {
              title: 'Tiền vàng',
              dataIndex: 'totalGoldPrice',
              align: 'right',
              currency: 'vi-VN',
              width: 100,
              render: (value, record: any) => {
                const isValid =
                  value !== null &&
                  value !== undefined &&
                  record?.orderTempDetail[0]?.tproduct?.ttype?.id === 'T';

                return isValid
                  ? formatCurrency({
                      value,
                      round: true,
                    })
                  : '-';
              },
            },
            {
              title: 'Thành tiền món',
              dataIndex: 'totalM',
              align: 'right',
              currency: 'vi-VN',
              width: 100,
              render: (_, record: any) => {
                const finalPrice = record?.finalPriceSap || record?.finalPrice;
                const isValid =
                  finalPrice !== null &&
                  finalPrice !== undefined &&
                  record?.orderTempDetail[0]?.tproduct?.ttype?.id !== 'T';

                return isValid
                  ? formatCurrency({
                      value: finalPrice || 0,
                      round: true,
                    })
                  : '-';
              },
            },
            {
              title: 'Thành tiền công',
              dataIndex: 'totalC',
              align: 'right',
              currency: 'vi-VN',
              className: 'font-bold',
              width: 100,
              render: (_, record: any) => {
                const finalPrice = record?.finalPriceSap || record?.finalPrice;
                const isValid =
                  finalPrice !== null &&
                  finalPrice !== undefined &&
                  record?.orderTempDetail[0]?.tproduct?.ttype?.id === 'T';

                return isValid
                  ? formatCurrency({
                      value: finalPrice || 0,
                      round: true,
                    })
                  : '-';
              },
            },
            {
              title: 'Chiết khấu',
              align: 'right',
              currency: 'vi-VN',
              width: 100,
              render: () =>
                orderData?.totalDiscount
                  ? `${formatCurrency({
                      value: orderData?.totalDiscount,
                      round: true,
                    })}`
                  : 0,
            },
          ],
          data: [goldSoldBuyOrderData],
        },
        orderData.orderTypeId === 'A109_01'
          ? {
              key: 'total gold sold',
              title: 'Tổng lượng vàng xuất bán (chỉ)',
              tableProps: {
                scroll: { x: 2000 },
                // summary: (data: GoldenTBuy[]) => {
                //   console.log('total gold sold', data);
                //   const total = totalData(data, goldSoldTotalKeys);

                //   return (
                //     <Table.Summary.Row>
                //       <Table.Summary.Cell index={0}>
                //         <p className="pl-[20px] font-bold">Tổng cộng</p>
                //       </Table.Summary.Cell>
                //       <Table.Summary.Cell index={1}>-</Table.Summary.Cell>
                //       {Object.keys(total).map((key, index) => (
                //         <Table.Summary.Cell index={index + 2} key={index}>
                //           <p className="font-bold">
                //             {goldSoldNumberKeys.includes(key)
                //               ? total[key]
                //                 ? // ? Number(total[key]).toFixed(3)
                //                   formatNumber(total[key])
                //                 : total[key]
                //               : total[key]}
                //           </p>
                //         </Table.Summary.Cell>
                //       ))}
                //     </Table.Summary.Row>
                //   );
                // },
                summary: () => {
                  const { orderTempDetail, goldenTBuy } =
                    goldSoldBuyOrderData as IGoldSoldBuyOrder;

                  const sumGoldenTBuy = goldenTBuy?.reduce(
                    (sum, item) => {
                      if (item.sloc != null) {
                        sum.goldenWeightThread += Number(
                          item.goldenWeightThread.toFixed(3),
                        );
                        sum.goldWeightOfStool += Number(
                          item.goldWeightOfStool.toFixed(3),
                        );
                        sum.hao += item.hao;
                        sum.itemWeightOfStool += Number(
                          item.itemWeightOfStool.toFixed(3),
                        );
                        sum.itemWeightSeed += Number(
                          item.itemWeightSeed.toFixed(3),
                        );
                        sum.q10 += item.q10;
                        sum.quantity += item.quantity;
                      }
                      return sum;
                    },
                    {
                      goldenWeightThread: 0,
                      goldWeightOfStool: 0,
                      hao: 0,
                      itemWeightOfStool: 0,
                      itemWeightSeed: 0,
                      q10: 0,
                      quantity: 0,
                    },
                  );

                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <p className="pl-[20px] font-bold">Tổng cộng</p>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>-</Table.Summary.Cell>
                      {goldSoldTotalKeys?.map((key, index) => {
                        const dataKey = key as keyof typeof sumGoldenTBuy;

                        return (
                          <Table.Summary.Cell index={index + 2} key={index}>
                            <p className="font-bold">
                              {goldSoldNumberKeys.includes(key)
                                ? sumGoldenTBuy[dataKey]
                                  ? formatCurrency({
                                      value: Number(
                                        sumGoldenTBuy[dataKey].toFixed(3),
                                      ),
                                      format: 3,
                                    })
                                  : sumGoldenTBuy[dataKey]
                                : sumGoldenTBuy[dataKey]}
                            </p>
                          </Table.Summary.Cell>
                        );
                      })}
                    </Table.Summary.Row>
                  );
                },
                rowKey: (record) => record?.sloc ?? '',
              },
              columns: [
                {
                  title: 'Kho',
                  dataIndex: 'sloc',
                  className: 'text-start align-top !pl-[30px]',
                  width: 50,
                },
                {
                  title: 'Tuổi vàng',
                  dataIndex: 'age',
                  className: 'text-start align-top',
                  width: 50,
                },
                {
                  title: 'Tổng món',
                  dataIndex: 'quantity',
                  className: 'text-start align-top',
                  width: 50,
                },
                {
                  title: 'T. Lượng hột',
                  dataIndex: 'itemWeightSeed',
                  className: 'text-start align-top',
                  width: 50,
                  // render: (value) => (value ? formatNumber(value) : '-'),
                  render: (value) =>
                    value
                      ? formatCurrency({
                          value: Number(Number(value).toFixed(3)),
                          format: 3,
                        })
                      : // formatNumber(value)
                        value !== null
                        ? value
                        : '-',
                },
                {
                  title: 'T. Lượng vàng',
                  dataIndex: 'goldWeightOfStool',
                  className: 'text-start align-top',
                  width: 50,
                  // render: (value) => (value ? formatNumber(value) : '-'),
                  render: (value) =>
                    value
                      ? formatCurrency({
                          value: Number(Number(value).toFixed(3)),
                          format: 3,
                        })
                      : // formatNumber(value)
                        value !== null
                        ? value
                        : '-',
                },
                {
                  title: 'T.L Vàng + Hột',
                  dataIndex: 'itemWeightOfStool',
                  className: 'text-start align-top',
                  width: 50,
                  // render: (value) => (value ? formatNumber(value) : '-'),
                  render: (value) =>
                    value
                      ? formatCurrency({
                          value: Number(Number(value).toFixed(3)),
                          format: 3,
                        })
                      : // formatNumber(value)
                        value !== null
                        ? value
                        : '-',
                },
                {
                  title: 'Hao',
                  dataIndex: 'hao',
                  className: 'text-start align-top',
                  width: 50,
                  render: (value) =>
                    value
                      ? formatCurrency({
                          value: Number(Number(value).toFixed(3)),
                          format: 3,
                        })
                      : // formatNumber(value)
                        value !== null
                        ? value
                        : '-',
                },
                {
                  title: 'Vàng Q10',
                  dataIndex: 'q10',
                  className: 'text-start align-top',
                  width: 50,
                  render: (value) =>
                    value
                      ? formatCurrency({
                          value: Number(Number(value).toFixed(3)),
                          format: 3,
                        })
                      : value !== null
                        ? value
                        : '-',
                },
              ],
              data:
                (goldSoldBuyOrderData as IGoldSoldBuyOrder)?.goldenTBuy ?? [],
            }
          : {
              key: 'total gold sold',
              title: 'Tổng lượng vàng xuất bán (chỉ)',
              tableProps: {
                scroll: { x: 2000 },
                // summary: (data: GoldenYBuy[]) => {
                //   const total = totalData(
                //     data,
                //     [
                //       'itemWeightOfStool',
                //       'goldWeightOfStool',
                //       'a',
                //       'value',
                //       'quantity',
                //     ],
                //     [
                //       'sloc',
                //       'age',
                //       'itemWeightOfStool',
                //       'goldWeightOfStool',
                //       'a',
                //       'value',
                //       'quantity',
                //     ],
                //   );

                //   return (
                //     <Table.Summary.Row>
                //       <Table.Summary.Cell index={0}>
                //         <p className="pl-[20px] font-bold">Tổng cộng</p>
                //       </Table.Summary.Cell>
                //       {/* <Table.Summary.Cell index={1}>-</Table.Summary.Cell> */}
                //       {Object.keys(total).map((key, index) => (
                //         <Table.Summary.Cell index={index + 2} key={index}>
                //           <p className="font-bold">
                //             {['value'].includes(key)
                //               ? formatCurrency({
                //                   value: total[key],
                //                   round: true,
                //                 })
                //               : [
                //                     'a',
                //                     'itemWeightOfStool',
                //                     'goldWeightOfStool',
                //                   ].includes(key)
                //                 ? formatNumber(total[key])
                //                 : total[key]}
                //           </p>
                //         </Table.Summary.Cell>
                //       ))}
                //     </Table.Summary.Row>
                //   );
                // },
                summary: () => {
                  const { orderTempDetail, goldenYBuy } =
                    goldSoldBuyOrderData as IGoldSoldYBuyOrder;

                  const dataIndexes = [
                    // 'sloc',
                    // 'age',
                    // 'totalItemWeightOfStool',
                    // 'totalGoldWeightOfStool',
                    // 'totalDiscountAmount',
                    // 'totalValue',
                    // 'totalQuantity',

                    'age',
                    'itemWeightThread',
                    'goldenWeightThread',
                    'itemWeightSeed',
                    'value',
                    'quantity',
                  ];
                  const numberKey = [
                    // 'totalItemWeightOfStool',
                    // 'totalGoldWeightOfStool',
                    // 'totalDiscountAmount',
                    'age',
                    'itemWeightThread',
                    'goldenWeightThread',
                    'itemWeightSeed',
                    'value',
                    // 'quantity',
                  ];

                  const sumGoldenYBuy = goldenYBuy?.reduce(
                    (sum, item) => {
                      if (item.sloc != null) {
                        sum.goldenWeightThread += Number(
                          item.goldenWeightThread.toFixed(3),
                        );
                        sum.goldWeightOfStool += Number(
                          item.goldWeightOfStool.toFixed(3),
                        );
                        sum.value += item.value;
                        sum.itemWeightOfStool += Number(
                          item.itemWeightOfStool.toFixed(3),
                        );
                        sum.itemWeightThread += Number(
                          item.itemWeightThread.toFixed(3),
                        );
                        sum.itemWeightGram += item.itemWeightGram;
                        sum.quantity += item.quantity;
                        sum.itemWeightSeed += item.itemWeightSeed;
                      }
                      return sum;
                    },
                    {
                      goldenWeightThread: 0,
                      goldWeightOfStool: 0,
                      itemWeightOfStool: 0,
                      value: 0,
                      itemWeightSeed: 0,
                      itemWeightThread: 0,
                      itemWeightGram: 0,
                      quantity: 0,
                    },
                  );

                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <p className="pl-[20px] font-bold">Tổng cộng</p>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>-</Table.Summary.Cell>
                      {dataIndexes?.map((key, index) => {
                        const dataKey = key as keyof typeof sumGoldenYBuy;

                        return (
                          <Table.Summary.Cell index={index + 2} key={index}>
                            <p className="font-bold">
                              {numberKey.includes(key)
                                ? sumGoldenYBuy[dataKey]
                                  ? formatCurrency({
                                      value: Number(
                                        sumGoldenYBuy[dataKey].toFixed(3),
                                      ),

                                      format: 3,
                                      round: dataKey === 'value' ? true : false,
                                    })
                                  : sumGoldenYBuy[dataKey]
                                : sumGoldenYBuy[dataKey]}
                            </p>
                          </Table.Summary.Cell>
                        );
                      })}
                    </Table.Summary.Row>
                  );
                },
                rowKey: (record) => record?.sloc ?? '',
              },
              columns: [
                {
                  title: 'NHÓM',
                  dataIndex: 'groupPrice',
                  className: 'text-start align-top !pl-[30px]',
                  width: 50,
                },
                {
                  title: 'Kho',
                  dataIndex: 'sloc',
                  className: 'text-start align-top !pl-[30px]',
                  width: 50,
                },
                {
                  title: 'Tuổi vàng',
                  dataIndex: 'age',
                  className: 'text-start align-top',
                  width: 50,
                  render: (value) => value || 0,
                },
                {
                  title: 'T.Lượng Món',
                  dataIndex: 'itemWeightThread',
                  className: 'text-start align-top',
                  width: 50,
                  render: (value) =>
                    value
                      ? formatCurrency({
                          value: Number(value.toFixed(3)),
                          format: 3,
                        })
                      : value !== null
                        ? value
                        : '-',
                },
                {
                  title: 'T.Lượng vàng',
                  dataIndex: 'goldenWeightThread',
                  className: 'text-start align-top',
                  width: 50,
                  render: (value) =>
                    value
                      ? formatCurrency({
                          value: Number(value.toFixed(3)),
                          format: 3,
                        })
                      : value !== null
                        ? value
                        : '-',
                },
                {
                  title: 'T.Lượng hột',
                  dataIndex: 'itemWeightSeed',
                  className: 'text-start align-top',
                  width: 50,
                  render: (value) =>
                    value
                      ? formatCurrency({
                          value: Number(value.toFixed(3)),
                          format: 3,
                        })
                      : value !== null
                        ? value
                        : '-',
                },
                {
                  title: 'Trị giá',
                  dataIndex: 'value',
                  className: 'text-start align-top',
                  width: 50,
                  render: (value) =>
                    value
                      ? formatCurrency({ value: Number(value.toFixed(3)) })
                      : value !== null
                        ? value
                        : '-',
                },
                {
                  title: 'Tổng món',
                  dataIndex: 'quantity',
                  className: 'text-start align-top',
                  width: 50,
                },
              ],
              data:
                (goldSoldBuyOrderData as IGoldSoldYBuyOrder)?.goldenYBuy ?? [],
            },
        orderData.orderTypeId === 'A109_01'
          ? {
              key: 'sell',
              title: 'Bán hàng',
              data: goldSoldBuyOrder,
              tableProps: {
                scroll: { x: 2500 },
                pagination: {
                  current: pageInfo.page,
                  pageSize: pageInfo.pageSize,
                  total: goldSoldBuyOrderData?.orderTempDetail?.length ?? 0,
                  onChange: (page, pageSize) => setPageInfo({ page, pageSize }),
                },
                // summary: () => {
                //   const data = goldSoldBuyOrderData?.orderTempDetail ?? [];

                //   if (data.length <= 0) return;
                //   const total = totalData(data, sellTotalKeys, sellDataIndexes);

                //   return (
                //     <Table.Summary.Row>
                //       <Table.Summary.Cell index={0}>
                //         <p className="font-bold pl-[15px]">Tổng cộng</p>
                //       </Table.Summary.Cell>
                //       {sellDataIndexes.map((dataIndex, index) => (
                //         <Table.Summary.Cell index={index + 1} key={index}>
                //           <p
                //             className={`font-bold ${formatSellDataIndex[dataIndex]}`}
                //           >
                //             {sellNumberKeys.includes(dataIndex)
                //               ? formatNumber(total[dataIndex])
                //               : [...sellMoneyKeys, 'q10'].includes(dataIndex)
                //                 ? formatCurrency({
                //                     value: total[dataIndex],
                //                     round: true,
                //                   })
                //                 : (total[dataIndex] ?? '-')}
                //           </p>
                //         </Table.Summary.Cell>
                //       ))}
                //     </Table.Summary.Row>
                //   );
                // },
                summary: () => {
                  const { orderTempDetail, ...rest } = goldSoldBuyOrderData;

                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <p className="font-bold pl-[15px]">Tổng cộng</p>
                      </Table.Summary.Cell>
                      {sellDataIndexes.map((dataIndex, index) => {
                        const dataKey = dataIndex as keyof typeof rest;

                        return (
                          <Table.Summary.Cell index={index + 1} key={index}>
                            <p
                              className={`font-bold ${formatSellDataIndex[dataIndex]}`}
                            >
                              {sellNumberKeys.includes(dataIndex)
                                ? formatNumber(rest[dataKey])
                                : [...sellMoneyKeys, 'q10'].includes(dataIndex)
                                  ? formatCurrency({
                                      value: rest[dataKey],
                                      round: true,
                                    })
                                  : (rest[dataKey] ?? '-')}
                            </p>
                          </Table.Summary.Cell>
                        );
                      })}
                    </Table.Summary.Row>
                  );
                },
              },
              columns: [
                {
                  title: 'STT',
                  dataIndex: 'stt',
                  className: 'text-end align-top !pl-[15px]',
                  align: 'center',
                  render: (_: any, __: any, index: number) =>
                    index + 1 + pageInfo.pageSize * (pageInfo.page - 1),
                },
                {
                  title: 'Hình ảnh',
                  dataIndex: ['tproduct', 'urlImage1'],
                  className: 'text-start align-top',
                  width: 100,
                  render: (value) => {
                    console.log('hình', value);
                    return <Image src={value} height={50} width={50} />;
                  },
                },
                {
                  title: 'Mã sản phẩm',
                  dataIndex: ['tproduct', 'id'],
                  className: 'text-start align-top',
                },
                {
                  title: 'Tên sản phẩm',
                  dataIndex: ['tproduct', 'name'],
                  className: 'text-start align-top',
                },
                {
                  //
                  title: 'Trùng mã/Batch',
                  dataIndex: '1',
                  className: 'text-start align-top',
                  render: (_, record) => {
                    const data = goldSoldBuyOrder.filter(
                      (item) => item.id !== record?.id,
                    );
                    const isDuplicateBatchIndex = data.findIndex(
                      (item) =>
                        item?.tstorageLocation?.batch ===
                        record?.tstorageLocation?.batch,
                    );

                    return isDuplicateBatchIndex >= 0 ? 'Trùng' : '-';
                  },
                },
                {
                  //
                  title: 'Lỗi',
                  dataIndex: '2',
                  className: 'text-start align-top',
                  render: (_, record) => {
                    return (record.checkInventory ?? true) == true
                      ? '_'
                      : 'Sản phẩm này đã hết hàng';
                  },
                },
                {
                  title: 'Tiền vàng',
                  dataIndex: 'goldPrice',
                  align: 'right',
                  currency: 'vi-VN',
                  render: (value) => {
                    return formatCurrency({
                      value,
                      round: true,
                    });
                  },
                },
                {
                  title: 'Đ/ Giá công',
                  dataIndex: 'processPrice',
                  align: 'right',
                  currency: 'vi-VN',
                  render: (value) => {
                    return formatCurrency({
                      value,
                      round: true,
                    });
                  },
                },
                {
                  title: 'Đ/ Giá món',
                  dataIndex: 'totalPrice',
                  align: 'right',
                  currency: 'vi-VN',
                  render: (value) => {
                    return value
                      ? formatCurrency({
                          value,
                          round: true,
                        })
                      : value;
                  },
                },
                {
                  title: 'Thành tiền',
                  dataIndex: 'finalPrice',
                  align: 'right',
                  currency: 'vi-VN',
                  render: (value) =>
                    formatCurrency({
                      value,
                      round: true,
                    }),
                },
                {
                  title: 'Tiền hao lai',
                  dataIndex: 'haolaiCost',
                  className: 'text-start align-top',
                  render: (value) => formatCurrency({ value }),
                },
                {
                  title: 'Tuổi',
                  dataIndex: ['tproduct', 'age'],
                  className: 'text-start align-top',
                },
                {
                  title: 'T.L hột',
                  dataIndex: 'itemWeightSeed',
                  className: 'text-start align-top',
                  render: (value) => (value ? formatNumber(value) : 0),
                },
                {
                  title: 'T.L vàng',
                  dataIndex: 'goldWeightOfStool',
                  className: 'text-start align-top',
                  render: (value) => (value ? formatNumber(value) : 0),
                },
                {
                  title: 'Đ/G công sau CK',
                  dataIndex: 'processPriceSap',
                  align: 'right',
                  currency: 'vi-VN',
                  render: (value) => {
                    return value
                      ? formatCurrency({
                          value,
                          round: true,
                        })
                      : 0;
                  },
                },
                {
                  title: 'Đ/G món sau CK',
                  dataIndex: 'totalPriceSap',
                  align: 'right',
                  currency: 'vi-VN',
                  render: (value) => {
                    return value
                      ? formatCurrency({
                          value,
                          round: true,
                        })
                      : 0;
                  },
                },
                {
                  title: 'Thành tiền sau CK',
                  dataIndex: 'finalPriceSap',
                  align: 'right',
                  currency: 'vi-VN',
                  render: (value) => {
                    return value
                      ? formatCurrency({
                          value,
                          round: true,
                        })
                      : 0;
                  },
                },
                {
                  title: 'Số món',
                  dataIndex: 'quantity',
                  className: 'text-start align-top',
                },
                {
                  title: 'Batch',
                  dataIndex: ['tstorageLocation', 'batch'],
                  className: 'text-start align-top',
                },
                {
                  title: 'Sloc',
                  dataIndex: ['tstorageLocation', 'sloc'],
                  className: 'text-start align-top',
                },

                {
                  title: 'CTKM',
                  dataIndex: 'voucherName',
                  className: 'text-start align-top',
                },

                {
                  title: 'Ngày tạo',
                  width: 160,
                  dataIndex: 'createDate',
                  className: 'text-start align-top',
                  type: 'date',
                  render: (value) => {
                    return value && formatDate(value);
                  },
                },
                {
                  title: 'Người cập nhật',
                  width: 125,
                  dataIndex: ['tmodifyUser', 'fullName'],
                  className: 'text-start align-top',
                },
                {
                  title: 'Ngày cập nhật',
                  width: 160,
                  dataIndex: 'modifyDate',
                  className: 'text-start align-top',
                  type: 'date',
                  render: (value) => {
                    return value && formatDate(value);
                  },
                },
              ],
              // data: goldSoldBuyOrderData?.orderTempDetail ?? [],
            }
          : {
              key: 'sell',
              title: 'Bán hàng',
              data: goldSoldBuyOrder,
              tableProps: {
                scroll: { x: 4000 },
                pagination: {
                  current: pageInfo.page,
                  pageSize: pageInfo.pageSize,
                  total: goldSoldBuyOrderData?.orderTempDetail?.length ?? 0,
                  onChange: (page, pageSize) => setPageInfo({ page, pageSize }),
                },
                // summary: () => {
                //   const data = (
                //     (goldSoldBuyOrderData?.orderTempDetail ??
                //       []) as OrderTempDetailY[]
                //   ).map((item) => ({
                //     ...item,
                //     itemWeightOfStool:
                //       item?.tstorageLocation?.itemWeightOfStool,
                //   }));

                //   if (data.length <= 0) return;
                //   const dataIndex = [
                //     'urlImage',
                //     'id',
                //     'name',
                //     'age',
                //     'goldWeightOfStool',
                //     'finalPriceSap',
                //     'quantity',
                //     'itemWeightOfStool',
                //     'itemWeightGram',
                //     '1',
                //     'discountAmount',
                //     'finalPrice',
                //     'batch',
                //     'sloc',
                //     '2',
                //     'voucherName',
                //     '3',
                //     'createDate',
                //     'tmodifyUser',
                //     'modifyDate',
                //   ];
                //   const total = totalData(
                //     data,
                //     [
                //       'goldWeightOfStool',
                //       'finalPriceSap',
                //       'quantity',
                //       'itemWeightOfStool',
                //       'itemWeightGram',
                //       'discountAmount',
                //       'finalPrice',
                //     ],
                //     dataIndex,
                //   );
                //   const formatSellDataIndex = {
                //     'discountAmount': 'text-right',
                //     'finalPrice': 'text-right',
                //     'finalPriceSap': 'text-right',
                //     '1': 'text-right',
                //   } as any;

                //   return (
                //     <Table.Summary.Row>
                //       <Table.Summary.Cell index={0}>
                //         <p className="font-bold pl-[15px]">Tổng cộng</p>
                //       </Table.Summary.Cell>
                //       {dataIndex.map((dataIndex, index) => (
                //         <Table.Summary.Cell index={index + 1} key={index}>
                //           <p
                //             className={`font-bold ${formatSellDataIndex[dataIndex]}`}
                //           >
                //             {[
                //               'goldWeightOfStool',
                //               'itemWeightOfStool',
                //               'itemWeightGram',
                //             ].includes(dataIndex)
                //               ? // ? Number(total[dataIndex]).toFixed(3)
                //                 formatNumber(total[dataIndex])
                //               : [
                //                     'discountAmount',
                //                     'finalPrice',
                //                     'finalPriceSap',
                //                   ].includes(dataIndex)
                //                 ? formatCurrency({
                //                     value: total[dataIndex],
                //                     round: true,
                //                   })
                //                 : (total[dataIndex] ?? '-')}
                //           </p>
                //         </Table.Summary.Cell>
                //       ))}
                //     </Table.Summary.Row>
                //   );
                // },
                summary: () => {
                  const { orderTempDetail, ...rest } = goldSoldBuyOrderData;

                  const dataIndex = [
                    'urlImage',
                    'id',
                    'name',
                    '2',
                    '3',
                    'age',
                    'totalGoldWeightOfStool',
                    'finalPriceSap',
                    'totalQuantity',
                    'totalItemWeightOfStool',
                    'totalItemWeightGram',
                    '1',
                    'totalDiscountAmount',
                    'finalPrice',
                    'totalProcessPrice',
                    'batch',
                    'sloc',

                    'voucherName',

                    'createDate',
                    'tmodifyUser',
                    'modifyDate',
                  ];
                  const totalKey = [
                    'totalGoldWeightOfStool',
                    'finalPriceSap',
                    'totalQuantity',
                    'totalItemWeightOfStool',
                    'totalItemWeightGram',
                    'totalDiscountAmount',
                    'finalPrice',
                    'totalProcessPrice',
                  ];
                  const valueKey = [
                    'finalPriceSap',
                    'finalPrice',
                    'totalProcessPrice',
                  ];
                  const keyRightFormat = [
                    ...valueKey,
                    '1',
                    'totalDiscountAmount',
                  ];

                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <p className="font-bold pl-[15px]">Tổng cộng</p>
                      </Table.Summary.Cell>
                      {dataIndex.map((key, index) => {
                        const dataKey = key as keyof typeof rest;

                        return (
                          <Table.Summary.Cell key={index} index={index + 1}>
                            <p
                              className={classNames(
                                'font-bold',
                                keyRightFormat.includes(key)
                                  ? 'text-right'
                                  : '',
                              )}
                            >
                              {totalKey.includes(key)
                                ? valueKey.includes(key)
                                  ? formatCurrency({
                                      value: goldSoldBuyOrderData[dataKey],
                                      round: true,
                                    })
                                  : formatNumber(goldSoldBuyOrderData[dataKey])
                                : '-'}
                            </p>
                          </Table.Summary.Cell>
                        );
                      })}
                      {/* {dataIndex.map((dataIndex, index) => (
                        <Table.Summary.Cell index={index + 1} key={index}>
                          <p
                            className={`font-bold ${formatSellDataIndex[dataIndex]}`}
                          >
                            {[
                              'goldWeightOfStool',
                              'itemWeightOfStool',
                              'itemWeightGram',
                            ].includes(dataIndex)
                              ? formatNumber(total[dataIndex])
                              : [
                                    'discountAmount',
                                    'finalPrice',
                                    'finalPriceSap',
                                  ].includes(dataIndex)
                                ? formatCurrency({
                                    value: total[dataIndex],
                                    round: true,
                                  })
                                : (total[dataIndex] ?? '-')}
                          </p>
                        </Table.Summary.Cell>
                      ))} */}
                    </Table.Summary.Row>
                  );
                },
              },
              columns: [
                {
                  width: 150,
                  title: 'STT',
                  dataIndex: 'stt',
                  align: 'center',
                  className: 'text-end align-top !pl-[15px]',
                  render: (_: any, __: any, index: number) =>
                    index + 1 + pageInfo.pageSize * (pageInfo.page - 1),
                },
                {
                  title: 'Hình ảnh',
                  dataIndex: ['tproduct', 'urlImage'],
                  className: 'text-start align-top',
                  render: (value) => {
                    return <Image src={value} height={50} width={50} />;
                  },
                },
                {
                  title: 'Mã sản phẩm',
                  dataIndex: ['tproduct', 'id'],
                  className: 'text-start align-top',
                },
                {
                  title: 'Tên sản phẩm',
                  dataIndex: ['tproduct', 'name'],
                  className: 'text-start align-top',
                },
                {
                  //
                  title: 'Trùng mã/Batch',
                  dataIndex: '1',
                  className: 'text-start align-top',
                  render: (_, record) => {
                    const data = goldSoldBuyOrder.filter(
                      (item) => item.id !== record?.id,
                    );
                    const isDuplicateBatchIndex = data.findIndex(
                      (item) =>
                        item?.tstorageLocation?.batch ===
                        record?.tstorageLocation?.batch,
                    );

                    return isDuplicateBatchIndex >= 0 ? 'Trùng' : '-';
                  },
                },
                {
                  //
                  title: 'Lỗi',
                  dataIndex: '2',
                  className: 'text-start align-top',
                  render: (_, record) => {
                    return (record.checkInventory ?? true) == true
                      ? '_'
                      : 'Sản phẩm này đã hết hàng';
                  },
                },
                {
                  title: 'Tuổi',
                  dataIndex: ['tproduct', 'age'],
                  className: 'text-start align-top',
                },
                {
                  width: 150,
                  title: 'T.L vàng (phân)',
                  dataIndex: 'goldWeightOfStool',
                  className: 'text-start align-top',
                  render: (value) => (value ? formatNumber(value) : 0),
                },
                {
                  width: 150,
                  title: 'Thành tiền sau CK',
                  dataIndex: 'finalPriceSap',
                  align: 'right',
                  currency: 'vi-VN',
                  render: (value) => {
                    return value
                      ? formatCurrency({
                          value,
                          round: true,
                        })
                      : 0;
                  },
                },
                {
                  title: 'Số món',
                  dataIndex: 'quantity',
                  className: 'text-start align-top',
                },
                {
                  width: 150,
                  title: 'T.L món (phân)',
                  dataIndex: ['tstorageLocation', 'itemWeightOfStool'],
                  className: 'text-start align-top',
                  render: (value) => (value ? formatNumber(value) : 0),
                },
                {
                  width: 150,
                  title: 'T.L món (gram)',
                  dataIndex: 'itemWeightGram',
                  className: 'text-start align-top',
                  render: (value) => (value ? formatNumber(value) : 0),
                },
                {
                  title: '(Nhóm giá) ĐG bán (phân)',
                  align: 'right',
                  currency: 'vi-VN',
                  render: (_, record) => {
                    return `(${record?.intGroupPrice}) ${formatCurrency({
                      value: record?.unitPriceByThread,
                      round: true,
                    })}`;
                  },
                },
                {
                  title: 'Chiết khấu',
                  dataIndex: 'discountAmount',
                  align: 'right',
                  currency: 'vi-VN',
                  render: (value) => {
                    return formatCurrency({
                      value,
                      round: true,
                    });
                  },
                },
                {
                  width: 200,
                  title: 'Thành tiền trước CK',
                  dataIndex: 'finalPrice',
                  align: 'right',
                  currency: 'vi-VN',
                  render: (value) => {
                    return value
                      ? formatCurrency({
                          value,
                          round: true,
                        })
                      : 0;
                  },
                },
                {
                  width: 150,
                  title: 'Tiền công món',
                  align: 'right',
                  dataIndex: 'processPrice',
                  className: 'text-start align-top',
                  render: (value) => formatCurrency({ value }),
                },
                {
                  title: 'Batch',
                  dataIndex: ['tstorageLocation', 'batch'],
                  className: 'text-start align-top',
                },
                {
                  title: 'Sloc',
                  dataIndex: ['tstorageLocation', 'sloc'],
                  className: 'text-start align-top',
                },

                {
                  title: 'CTKM',
                  dataIndex: 'voucherName',
                  className: 'text-start align-top',
                },

                {
                  title: 'Ngày tạo',
                  width: 160,
                  dataIndex: 'createDate',
                  className: 'text-start align-top',
                  type: 'date',
                  render: (value) => {
                    return value && formatDate(value);
                  },
                },
                {
                  title: 'Người cập nhật',
                  width: 125,
                  dataIndex: ['tmodifyUser', 'fullName'],
                  className: 'text-start align-top',
                },
                {
                  title: 'Ngày cập nhật',
                  width: 160,
                  dataIndex: 'modifyDate',
                  className: 'text-start align-top',
                  type: 'date',
                  render: (value) => {
                    return value && formatDate(value);
                  },
                },
              ],
            },
      ] as IPanelConfig[];
    }, [
      user,
      customer,
      pageInfo,
      orderData,
      promotionData,
      hasSODOBilling,
      isChildrenOrder,
      goldSoldBuyOrder,
      isOrderOfAccountant,
      goldSoldBuyOrderData,
      salesLimitData,
      isGetSalesLimitPending,
    ]);

    const customerOptions = useMemo(() => {
      return customerList.map(
        (item) =>
          ({
            label: item?.id,
            value: item?.id,
          }) as DefaultOptionType,
      );
    }, [customerList]);

    useEffect(() => {
      const { page, pageSize } = pageInfo;

      const data = goldSoldBuyOrderData?.orderTempDetail ?? [];
      // .slice(
      //   (page - 1) * pageSize,
      //   (page - 1) * pageSize + 10,
      // );

      setGoldSoldBuyOrder(data);
    }, [pageInfo, goldSoldBuyOrderData]);

    useEffect(() => {
      if (!isOpenCustomerModal) return;

      const currentCustomer = customerList[0];

      form.setFieldsValue({
        ...currentCustomer,
        customerId: currentCustomer?.id,
      });
    }, [customerList, isOpenCustomerModal]);

    useEffect(() => {
      if (!orderData?.id) return;
      setEnableCreateOrder(false);
    }, [orderData?.id]);

    // useEffect(() => {
    //   if (promotionData?.IsAllocatePricePending) {
    //     setEnableCreateOrder(false);
    //   }
    // }, [promotionData?.IsAllocatePricePending]);
    useEffect(() => {
      if (!promotionData?.RequestTime || promotionData?.Option !== 'M') return;

      if (
        !promotionData?.IsAllocatePricePending &&
        promotionData?.RequestTime >= Date.now() - 180000
      )
        setEnableCreateOrder(true);
    }, [promotionData]);

    // Fetch sales limit data when customer is available
    useEffect(() => {
      console.log('🔍 Customer effect triggered:', customer?.id);
      console.log('🔍 OrderData plantId:', orderData?.plantId);
      if (customer?.id) {
        console.log(
          '🔍 Calling getSalesLimitMutate with customerId and plantId:',
          { customerId: customer.id, plantId: orderData?.plantId },
        );
        getSalesLimitMutate({
          customerId: customer.id,
          plantId: orderData?.plantId ?? '',
        });
      } else {
        console.log('🔍 No customer.id, setting salesLimitData to null');
        setSalesLimitData(null);
      }
    }, [customer?.id, orderData?.plantId]);

    // Timeout để tránh loading mãi mãi
    useEffect(() => {
      if (isGetSalesLimitPending) {
        const timeout = setTimeout(() => {
          console.log('🔍 Sales limit API timeout after 10 seconds');
          setSalesLimitData(null);
        }, 10000);

        return () => clearTimeout(timeout);
      }
    }, [isGetSalesLimitPending]);

    const handleOnCustomerModalCancel = useCallback(() => {
      setIsOpenCustomerModal(false);
    }, []);

    const handleOnCustomerModalOk = useCallback(() => {
      const formValues = form.getFieldsValue();

      if (
        orderData.childCustomerId !== null &&
        orderData.childCustomerId !== formValues?.customerId
      ) {
        updateOrderTempMutate({
          childCustomerId: formValues?.customerId,
          customerId: orderData?.customerId,
          id: orderData?.id,
        });
      }
    }, [orderData, customer]);

    const handleOnCustomerSelect = useCallback(
      (value: string) => {
        const currentCustomer = customerList.find(
          (customer) => customer?.id === value,
        );

        if (currentCustomer) form.setFieldsValue(currentCustomer);
      },
      [customerList],
    );

    const handleCreateOrder = useCallback(async () => {
      const conditionInsert: IPromotionResult['results'] = [];
      const conditionDelete: IPromotionResult['results'] = [];

      tableData.SAPPromotions.forEach((item) => {
        item.records.forEach((item) => {
          const condition = {
            CondCount: item.CondCount,
            ConditionType: item.ConditionType,
            ConditionValue: item.ConditionValue,
            Item: item.Item,
            Key: item.Key,
          };

          if (item.apply) {
            conditionInsert.push(condition);
            return;
          }

          conditionDelete.push(condition);
        });
      });

      const params = {
        ...promotionData,
        ConditionInsert: { results: conditionInsert },
        ConditionDelete: { results: conditionDelete },
        OutputDiscount: {
          results: (promotionData?.OutputDiscount?.results ?? []).map(
            (result) => {
              const isNotApply =
                conditionInsert.findIndex(
                  (condition) =>
                    condition?.Item === result?.Item &&
                    condition?.ConditionType === result?.ConditionType,
                ) < 0;

              return { ...result, apply: !isNotApply };
            },
          ),
        },
        PromoHeader: {
          results: [
            ...tableData.vouchers,
            ...tableData.manualVouchers.reduce((acc, item) => {
              if (item?.ConditionType.includes('H')) {
                item?.records.forEach((item) => {
                  try {
                    const { id, ...others } = item;
                    acc.push(others);
                  } catch {
                    acc.push(item);
                  }
                });
              }

              return acc;
            }, [] as any),
          ],
        },
        Voucher: {
          results: tableData.vouchers.map((item) => {
            return {
              ...item,
              Item: '000000',
              Key: orderData?.id,
            };
          }) as any,
        },
        PromoItem: {
          results: tableData.manualVouchers.reduce((acc, item) => {
            item.records.forEach((item) => {
              try {
                if (item?.ConditionType?.includes('I')) {
                  const { id, ...others } = item;
                  acc.push(others);
                }
              } catch (error) {
                console.log(error);
                acc.push(item);
              }
            });

            return acc;
          }, [] as Result[]),
        },
      } as IAllocatePriceParams;

      if (params.OrderType != 'ZWY') {
        const res = await PurChaseInvoiceApi.getMarketGoldPrice(
          convertDateToBE(dayjs()),
        );
        const marketGoldPrice = res?.data?.getDataMarketGoldPrice?.items?.[0];

        if (marketGoldPrice.salePrice != orderData?.marketGoldPrice) {
          toastError(`Giá vàng đã thay đổi, vui lòng lấy phân bổ lại!`);
          return;
        }

        params['GoldPrice'] = orderData?.marketGoldPrice.toString();
      }

      onCreateOrder({
        ...params,
        Option: 'M',
        UniqueCode: Date.now().toString(),
      });
    }, [tableData, promotionData, orderData, onCreateOrder]);

    const isDisabledButton = useMemo(() => {
      try {
        return (
          promotionData?.IsAllocatePricePending === true ||
          hasEInvoice ||
          (goldSoldBuyOrderData?.orderTempDetail ?? []).length <= 0 ||
          !promotionData?.OutputDiscount ||
          (promotionData?.Option === 'A' &&
            promotionData?.PendingMessage !== '')
        );
      } catch {
        return true;
      }
    }, [goldSoldBuyOrderData, promotionData, hasEInvoice]);

    const handleOnTakeAllocatePrice = useCallback(() => {
      const conditionInsert: IPromotionResult['results'] = [];
      const conditionDelete: IPromotionResult['results'] = [];

      tableData.SAPPromotions.forEach((item) => {
        item.records.forEach((item) => {
          const condition = {
            CondCount: item.CondCount,
            ConditionType: item.ConditionType,
            ConditionValue: item.ConditionValue,
            Item: item.Item,
            Key: item.Key,
          };

          if (item.apply) {
            conditionInsert.push(condition);
            return;
          }

          conditionDelete.push(condition);
        });
      });

      const params = {
        ...promotionData,
        Option: 'M',
        ConditionInsert: { results: conditionInsert },
        ConditionDelete: { results: conditionDelete },
        OutputDiscount: {
          results: (promotionData?.OutputDiscount?.results ?? []).map(
            (result) => {
              const isNotApply =
                conditionInsert.findIndex(
                  (condition) =>
                    condition?.Item === result?.Item &&
                    condition?.ConditionType === result?.ConditionType,
                ) < 0;

              return { ...result, apply: !isNotApply };
            },
          ),
        },
        PromoHeader: {
          results: [
            ...tableData.vouchers,
            ...tableData.manualVouchers.reduce((acc, item) => {
              if (item?.ConditionType.includes('H')) {
                item?.records.forEach((item) => {
                  try {
                    const { id, ...others } = item;
                    acc.push(others);
                  } catch {
                    acc.push(item);
                  }
                });
              }

              return acc;
            }, [] as any),
          ],
        },
        Voucher: {
          results: tableData.vouchers.map((item) => {
            return {
              ...item,
              Item: '000000',
              Key: orderData?.id,
            };
          }) as any,
        },
        PromoItem: {
          results: tableData.manualVouchers.reduce((acc, item) => {
            item.records.forEach((item) => {
              try {
                if (item?.ConditionType?.includes('I')) {
                  const { id, ...others } = item;
                  acc.push(others);
                }
              } catch (error) {
                console.log(error);
                acc.push(item);
              }
            });

            return acc;
          }, [] as Result[]),
        },
      } as IAllocatePriceParams;

      onTakeAllocatePrice({
        ...params,
        afterMutateSuccess: () => setEnableCreateOrder(true),
      });
    }, [tableData, promotionData, orderData, onTakeAllocatePrice]);

    return (
      <>
        <Row align={'middle'} justify={'end'}>
          {/* <Row align={'middle'} justify={'space-between'}> */}
          {/* <Col
            className={classNames(
              'w-max rounded-[100px] py-[4px] px-[11.5px]',
              orderStatus.status ? 'bg-[#EAF2FF] ' : 'bg-[#ffeaeb]',
            )}
          >
            <p className="text-sm font-normal">
              <span>Trạng thái:</span>{' '}
              <span
                className={classNames(
                  'font-bold',
                  orderStatus?.status ? 'text-[#0077CC]' : 'text-[#eb3455]',
                )}
              >
                {orderStatus?.message}
              </span>
            </p>
          </Col> */}
          <Col>
            <Row gutter={[9, 0]}>
              {!user?.isAdmin && (
                <Col>
                  <Button
                    title="Lấy giá phân bổ"
                    disabled={
                      isDisabledButton ||
                      hasSODOBilling ||
                      isChildrenOrder ||
                      !isOrderOfAccountant
                    }
                    handleOnclick={handleOnTakeAllocatePrice}
                  />
                </Col>
              )}
              {!user?.isAdmin && (
                <Col>
                  <Button
                    title="Tạo SO, DO, GI"
                    disabled={
                      !enableCreateOrder ||
                      promotionData?.IsAllocatePricePending ||
                      !promotionData?.OutputDiscount ||
                      promotionData?.PendingMessage !== '' ||
                      hasSODOBilling ||
                      isChildrenOrder ||
                      !isOrderOfAccountant ||
                      promotionData?.Option !== 'M'
                    }
                    handleOnclick={() =>
                      checkAndProceed(customer, handleCreateOrder)
                    }
                  />
                </Col>
              )}
              <Col className="h-[43px]">
                <ExportOptionsButton
                  menu={exportMenuProps}
                  disabled={Object.keys(goldSoldBuyOrderData || {}).length <= 0}
                />
                {/* <ExportOptionsButton
                  items={exportMenuItems}
                  disabled={Object.keys(goldSoldBuyOrderData || {}).length <= 0}
                /> */}
              </Col>
              {!user?.isAdmin && (
                <Col>
                  <OptionsMenu
                    menu={optionMenuProps}
                    disabled={
                      Object.keys(goldSoldBuyOrderData || {}).length <= 0
                    }
                  />
                </Col>
              )}
            </Row>
          </Col>
        </Row>
        <CustomCollapse panels={panels} isShowLength={false} />
        <Modal
          title="Khách hàng"
          open={isOpenCustomerModal}
          className="min-w-[1000px]"
          onCancel={handleOnCustomerModalCancel}
          footer={
            <div className="flex justify-end mt-4 gap-2">
              <Button
                handleOnclick={handleOnCustomerModalCancel}
                customClass="!border-transparent !text-neutral-30 !bg-transparent"
                title="Quay lại"
              />
              <Button
                fill
                title="Xác nhận"
                disabled={
                  isGetCustomerListPending || isUpdateOrderTempMutatePending
                }
                iconBefore={
                  isUpdateOrderTempMutatePending ? <LoadingOutlined /> : <></>
                }
                handleOnclick={handleOnCustomerModalOk}
              />
            </div>
          }
        >
          <Form form={form} layout="vertical">
            <Row gutter={{ xs: 8, sm: 14 }}>
              <Col span={colSpan}>
                <Form.Item
                  name="customerId"
                  label="Mã KH"
                  rules={[{ required: true }]}
                >
                  <SelectForm
                    className="mb-2"
                    placeholder="Chọn Chương trình"
                    loading={isGetCustomerListPending}
                    options={customerOptions}
                    onSelect={handleOnCustomerSelect}
                  />
                </Form.Item>
              </Col>
              <Col span={colSpan}>
                <Form.Item
                  name="storeName"
                  label="Tên KH"
                  rules={[{ required: true }]}
                >
                  <InputForm className="mb-2" readOnly placeholder="Tên KH" />
                </Form.Item>
              </Col>
              <Col span={colSpan}>
                <Form.Item
                  name="trankName"
                  label="Loại"
                  rules={[{ required: true }]}
                >
                  <InputForm className="mb-2" readOnly placeholder="Loại" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={{ xs: 8, sm: 14 }}>
              <Col span={colSpan}>
                <Form.Item
                  name="taxId"
                  label="MST"
                  rules={[{ required: true }]}
                >
                  <InputForm className="mb-2" readOnly placeholder="MST" />
                </Form.Item>
              </Col>
              <Col span={colSpan}>
                <Form.Item
                  name="mainCustomerId"
                  label="Người đại diện"
                  rules={[{ required: true }]}
                >
                  <InputForm
                    className="mb-2"
                    readOnly
                    placeholder="Người đại diện"
                  />
                </Form.Item>
              </Col>
              <Col span={colSpan}>
                <Form.Item
                  name="phone"
                  label="SDT"
                  rules={[{ required: true }]}
                >
                  <InputForm className="mb-2" readOnly placeholder="SDT" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={{ xs: 8, sm: 14 }}>
              <Col span={24}>
                <Form.Item
                  name="address"
                  label="Địa chỉ"
                  rules={[{ required: true }]}
                  labelCol={{ span: 24 }}
                >
                  <InputForm className="mb-2" readOnly placeholder="Địa chỉ" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
        {amlModalContext}
      </>
    );
  },
);
